import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { mixpanelService } from '../../services/mixpanel';
import { getPersonIdFromToken } from '../../services/tokenService';
import { useAppSelector } from '../../store/hooks';

function getRuntimeMixpanelToken(): string | null {
  const token = window.__CONFIG__?.MIXPANEL_PROJECT_TOKEN;
  if (typeof token !== 'string') return null;
  const trimmed = token.trim();
  return trimmed === '' ? null : trimmed;
}

export function useMixpanelInit(): void {
  const authStatus = useAppSelector((s) => s.auth.status);
  const isImpersonating = useAppSelector((s) => s.auth.impersonation?.active === true);
  const token = useAppSelector((s) => s.auth.token);
  const location = useLocation();

  // Extract Person UUID from JWT token (matches backend distinct_id)
  // This is stable and doesn't get overwritten by /users/me effect
  const personId = useMemo(() => {
    if (!token || authStatus !== 'authenticated') return null;
    return getPersonIdFromToken(token);
  }, [token, authStatus]);

  const sessionReplaySampleRate = 100;
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastIdentifiedPersonId, setLastIdentifiedPersonId] = useState<string | null>(null);

  // Detect admin pages - we don't record replay on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');

  // Initialize Mixpanel ONLY after authentication (once)
  useEffect(() => {
    // Guard: don't initialize if not authenticated or already initialized
    if (authStatus !== 'authenticated' || !personId || isInitialized || isImpersonating) {
      return;
    }

    // Poll for config if not available yet (handles race with config.js loading)
    const mixpanelToken = getRuntimeMixpanelToken();
    if (!mixpanelToken) {
      // Config not loaded yet - set up polling with event listener
      const onConfigLoaded = () => {
        const token = getRuntimeMixpanelToken();
        if (token && authStatus === 'authenticated' && personId && !isImpersonating) {
          const initialized = mixpanelService.initialize({
            token,
            enabled: true,
            sessionReplaySampleRate,
            debug: import.meta.env.DEV,
          });
          if (initialized) {
            setIsInitialized(true);
            mixpanelService.identify(personId);
            setLastIdentifiedPersonId(personId);
          }
        }
      };

      window.addEventListener('mento:config-loaded', onConfigLoaded);
      if (window.__CONFIG_LOADED__ === true) {
        onConfigLoaded();
      }

      return () => {
        window.removeEventListener('mento:config-loaded', onConfigLoaded);
      };
    }

    // Config is available - initialize immediately
    const initialized = mixpanelService.initialize({
      token: mixpanelToken,
      enabled: true,
      sessionReplaySampleRate,
      debug: import.meta.env.DEV,
    });

    if (initialized) {
      setIsInitialized(true);
      mixpanelService.identify(personId);
      setLastIdentifiedPersonId(personId);
    }
  }, [authStatus, personId, isInitialized, isImpersonating]);

  // Handle user identification changes (for subsequent logins after logout)
  useEffect(() => {
    // Only identify if Mixpanel is initialized and personId changed
    if (!isInitialized || !personId || isImpersonating || personId === lastIdentifiedPersonId) {
      return;
    }

    // New user logged in - identify them
    mixpanelService.identify(personId);

    // Only start recording if NOT on admin page
    if (!isAdminPage) {
      mixpanelService.startRecording();
    }

    setLastIdentifiedPersonId(personId);
  }, [personId, isInitialized, lastIdentifiedPersonId, isAdminPage, isImpersonating]);

  // Control recording based on current route (stop on admin, resume on non-admin)
  useEffect(() => {
    // Only control recording if Mixpanel is initialized and user is identified
    if (!isInitialized || !lastIdentifiedPersonId) {
      return;
    }

    if (isAdminPage || isImpersonating) {
      // Admin and impersonation pages must never be captured.
      mixpanelService.stopRecording();
    } else {
      // User navigated to non-admin page - resume recording
      mixpanelService.startRecording();
    }
  }, [isAdminPage, isImpersonating, isInitialized, lastIdentifiedPersonId]);

  // Reset on logout
  useEffect(() => {
    if (authStatus !== 'authenticated' && lastIdentifiedPersonId) {
      // Stop recording immediately to prevent capturing logout/login page with new device ID
      mixpanelService.stopRecording();
      mixpanelService.reset();
      setLastIdentifiedPersonId(null);
    }
  }, [authStatus, lastIdentifiedPersonId]);
}
